import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Avatar from "@/components/Avatar";
import {
  ClipboardList, Download, Coffee, Clock, CheckCircle2, Filter,
  Pencil, Trash2, ChevronDown, ChevronRight, Sparkles, CalendarDays, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate, todayIstIso } from "@/utils/date";
import { statusPill } from "@/constants/status";
import { ClientTag } from "@/components/ClientTag";

// Local date arithmetic in Asia/Kolkata (IST) so ranges match the user's calendar.
function isoDaysAgoIst(n) {
  const now = new Date(Date.now() - n * 86400000);
  return todayIstIso(now);
}
function weekdayLongIst(iso) {
  // Parse "YYYY-MM-DD" as a plain calendar date and print full weekday.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString("en-IN", { weekday: "long" });
}

/** Team Logs — every filed daily-log across the org, grouped day-wise with
 * a collapsible section per date. Today is expanded by default so managers
 * can see who's still pending. */
export default function TeamLogs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const today = todayIstIso();
  const [dateFrom, setDateFrom] = useState(isoDaysAgoIst(6)); // last 7 days by default
  const [dateTo, setDateTo] = useState(today);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  // taskById: {task_id: {title, start_date, deadline}} — used both for the
  // legacy-title fallback AND to determine whether a task was actually
  // scheduled for a given log_date. Managers wanted the Tasks column to only
  // list work that was ON THE PLATE for that day, not everything the person
  // ever updated.
  const [taskById, setTaskById] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [teamFilter, setTeamFilter] = useState("all");
  const [memberFilter, setMemberFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set([today])); // today open by default

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/tasks").then(({ data }) => {
      const map = {};
      (data || []).forEach((t) => {
        map[t.id] = {
          title: t.title,
          client: t.client || "",
          start_date: (t.start_date || "").slice(0, 10) || null,
          deadline: t.deadline ? t.deadline.slice(0, 10) : null,
        };
      });
      setTaskById(map);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/daily-logs", { params: { date_from: dateFrom, date_to: dateTo } });
      setLogs(data || []);
    } catch (e) {
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const teams = useMemo(() => Array.from(new Set(users.map((u) => u.team).filter(Boolean))), [users]);

  const rows = useMemo(() => {
    return logs
      .map((l) => ({
        ...l,
        user: userById[l.user_id] || { name: l.user_name || "Unknown", team: "", title: "", avatar_url: "" },
      }))
      .filter((r) => teamFilter === "all" || r.user.team === teamFilter)
      .filter((r) => memberFilter === "all" || r.user_id === memberFilter);
  }, [logs, userById, teamFilter, memberFilter]);

  // Group by log_date, then sort dates DESC. Inside each day, sort members alpha.
  const daysDesc = useMemo(() => {
    const bucket = new Map();
    rows.forEach((r) => {
      const d = r.log_date || "";
      if (!bucket.has(d)) bucket.set(d, []);
      bucket.get(d).push(r);
    });
    const dates = Array.from(bucket.keys()).sort((a, b) => b.localeCompare(a));
    return dates.map((d) => {
      const items = bucket.get(d).sort((a, b) =>
        (a.user.team || "").localeCompare(b.user.team || "") ||
        (a.user.name || "").localeCompare(b.user.name || "")
      );
      // Per-day stat chips
      const stats = {
        filed: items.length,
        leaves: items.filter((r) => r.on_leave).length,
        otHalf: items.filter((r) => r.overtime === "half" && !r.on_leave).length,
        otFull: items.filter((r) => r.overtime === "full" && !r.on_leave).length,
        otherWork: items.reduce((s, r) => s + (r.extra_tasks?.length || 0), 0),
      };
      return { date: d, items, stats };
    });
  }, [rows]);

  const totals = useMemo(() => ({
    filed: rows.length,
    leaves: rows.filter((r) => r.on_leave).length,
    otHalf: rows.filter((r) => r.overtime === "half" && !r.on_leave).length,
    otFull: rows.filter((r) => r.overtime === "full" && !r.on_leave).length,
    otherWork: rows.reduce((s, r) => s + (r.extra_tasks?.length || 0), 0),
  }), [rows]);

  // ---- Missing logs ----
  // For every working day in the selected range, flag teammates who still
  // owe their daily log. Weekends are skipped (not expected to file).
  // Scope follows the Team + Member filters so a manager can focus on a
  // specific squad or chase a single person.
  const scopedUsers = useMemo(() => {
    let xs = users.slice();
    if (teamFilter !== "all") xs = xs.filter((u) => u.team === teamFilter);
    if (memberFilter !== "all") xs = xs.filter((u) => u.id === memberFilter);
    // Super admins don't file daily logs — exclude them from the chasing list
    // so they don't show up as perpetual slackers.
    xs = xs.filter((u) => !u.is_super_admin);
    return xs;
  }, [users, teamFilter, memberFilter]);

  const filedByDateUser = useMemo(() => {
    const by = new Set();
    logs.forEach((l) => { by.add(`${l.log_date}|${l.user_id}`); });
    return by;
  }, [logs]);

  const missingByDate = useMemo(() => {
    if (!dateFrom || !dateTo || scopedUsers.length === 0) return [];
    const start = new Date(dateFrom + "T00:00:00");
    const end = new Date(dateTo + "T00:00:00");
    const out = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow === 0 || dow === 6) continue;         // skip weekends
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (iso > today) continue;                     // never flag future days
      const pending = scopedUsers.filter((u) => !filedByDateUser.has(`${iso}|${u.id}`));
      if (pending.length > 0) out.push({ date: iso, pending });
    }
    // Newest first — managers care most about the latest misses.
    return out.sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [dateFrom, dateTo, scopedUsers, filedByDateUser, today]);

  const totalMissing = useMemo(
    () => missingByDate.reduce((s, m) => s + m.pending.length, 0),
    [missingByDate]
  );

  const [missingOpen, setMissingOpen] = useState(true);

  const toggleDay = (d) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(daysDesc.map((d) => d.date)));
  const collapseAll = () => setExpanded(new Set());

  const openLog = (r) => nav(`/daily-log?user_id=${r.user_id}&date=${r.log_date}`);

  const deleteLog = async (r, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Delete ${r.user.name}'s log for ${r.log_date}? This cannot be undone.`)) return;
    try {
      await api.delete(`/daily-logs/${r.id}`);
      setLogs((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Log deleted");
    } catch {
      toast.error("Failed to delete log");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (teamFilter !== "all") params.team = teamFilter;
      if (memberFilter !== "all") params.user_id = memberFilter;
      const res = await api.get("/reports/team-logs-pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const suffix = memberFilter !== "all"
        ? "-" + (userById[memberFilter]?.name || "member").replace(/\W+/g, "-")
        : teamFilter !== "all"
        ? "-" + teamFilter.replace(/\W+/g, "-")
        : "";
      a.download = `LIM-TeamLogs-${dateFrom}_to_${dateTo}${suffix}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Team logs PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const setPreset = (days) => {
    setDateFrom(isoDaysAgoIst(days - 1));
    setDateTo(today);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap gap-4 justify-between items-end mb-6">
        <div>
          <div className="overline flex items-center gap-2"><ClipboardList size={12} /> Team Logs</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">Daily-log ledger</h1>
          <p className="text-sm text-white/50 mt-1">Every filed log across the team, grouped day by day. Click any date to expand.</p>
        </div>
        <Button
          data-testid="team-logs-download"
          onClick={downloadPdf}
          disabled={downloading || rows.length === 0}
          className="bg-red-600 hover:bg-red-500 rounded-full"
        >
          <Download size={14} className="mr-2" />
          {downloading ? "Generating…" : "Download PDF"}
        </Button>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="overline">From</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="team-logs-date-from"
            className="bg-black/40 border-white/10 mt-2 h-9 w-40"
          />
        </div>
        <div>
          <Label className="overline">To</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="team-logs-date-to"
            className="bg-black/40 border-white/10 mt-2 h-9 w-40"
          />
        </div>
        <div className="flex gap-2 items-end">
          <button onClick={() => setPreset(1)} className="text-xs uppercase tracking-widest px-2.5 h-9 rounded-full border border-white/10 text-white/70 hover:text-white hover:bg-white/5">Today</button>
          <button onClick={() => setPreset(7)} className="text-xs uppercase tracking-widest px-2.5 h-9 rounded-full border border-white/10 text-white/70 hover:text-white hover:bg-white/5">Last 7d</button>
          <button onClick={() => setPreset(30)} className="text-xs uppercase tracking-widest px-2.5 h-9 rounded-full border border-white/10 text-white/70 hover:text-white hover:bg-white/5">Last 30d</button>
        </div>
        {(user?.is_super_admin || user?.is_admin) && teams.length > 0 && (
          <div className="ml-auto">
            <Label className="overline">Team</Label>
            <div className="flex gap-1 mt-2 flex-wrap">
              <button
                onClick={() => setTeamFilter("all")}
                data-testid="team-logs-filter-all"
                className={`text-xs uppercase tracking-widest px-2.5 h-9 rounded-full border ${
                  teamFilter === "all" ? "bg-[#E11D2E]/15 border-[#E11D2E]/40 text-white" : "border-white/10 text-white/60 hover:text-white"
                }`}
              >
                <Filter size={10} className="inline -mt-0.5 mr-1" /> All
              </button>
              {teams.map((t) => (
                <button
                  key={t}
                  onClick={() => { setTeamFilter(t); setMemberFilter("all"); }}
                  className={`text-xs uppercase tracking-widest px-2.5 h-9 rounded-full border ${
                    teamFilter === t ? "bg-[#E11D2E]/15 border-[#E11D2E]/40 text-white" : "border-white/10 text-white/60 hover:text-white"
                  }`}
                >
                  {t.replace(" Team", "")}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="w-full sm:w-56">
          <Label className="overline">Member</Label>
          <select
            value={memberFilter}
            onChange={(e) => setMemberFilter(e.target.value)}
            data-testid="team-logs-member-filter"
            className="w-full bg-black/40 border border-white/10 rounded-md h-9 mt-2 px-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-red-500"
          >
            <option value="all">All members</option>
            {users
              .filter((u) => teamFilter === "all" || u.team === teamFilter)
              .sort((a, b) => (a.team || "").localeCompare(b.team || "") || a.name.localeCompare(b.name))
              .map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.title || u.role} · {u.team}</option>
              ))}
          </select>
        </div>
      </div>

      {/* Totals strip + expand/collapse */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        {[
          { label: "Logs filed", value: totals.filed, tone: "text-emerald-400", Icon: CheckCircle2 },
          { label: "On leave", value: totals.leaves, tone: "text-amber-300", Icon: Coffee },
          { label: "1/2 Day OT", value: totals.otHalf, tone: "text-[#E11D2E]", Icon: Clock },
          { label: "Full Day OT", value: totals.otFull, tone: "text-[#E11D2E]", Icon: Clock },
          { label: "Other work", value: totals.otherWork, tone: "text-white", Icon: Sparkles },
        ].map(({ label, value, tone, Icon }) => (
          <div key={label} className="glass rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="overline">{label}</div>
              <Icon size={12} className={tone} />
            </div>
            <div className={`font-hero text-2xl mt-1 ${tone}`}>{value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4 text-xs uppercase tracking-widest">
        <button
          onClick={expandAll}
          data-testid="team-logs-expand-all"
          className="px-2.5 h-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:bg-white/5"
        >
          Expand all
        </button>
        <button
          onClick={collapseAll}
          data-testid="team-logs-collapse-all"
          className="px-2.5 h-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:bg-white/5"
        >
          Collapse all
        </button>
        <div className="ml-auto text-white/40 font-mono normal-case tracking-normal">
          {daysDesc.length} day{daysDesc.length === 1 ? "" : "s"} · {rows.length} log{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* -------- MISSING LOGS -------- */}
      {/* Flags every teammate who still owes a daily log for a working day in
          the current range. Honours the Team + Member filters above so managers
          can zoom to their own squad. Collapsible — many managers prefer to
          read day-wise logs first and only expand this when chasing. */}
      {missingByDate.length > 0 && (
        <div
          className="glass rounded-xl overflow-hidden mb-4 border border-rose-500/30"
          data-testid="team-logs-missing-section"
        >
          <button
            type="button"
            onClick={() => setMissingOpen((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-3 text-left bg-rose-500/10 hover:bg-rose-500/15 transition-colors"
            data-testid="team-logs-missing-toggle"
          >
            {missingOpen
              ? <ChevronDown size={16} className="text-rose-300 shrink-0" />
              : <ChevronRight size={16} className="text-rose-300 shrink-0" />}
            <AlertTriangle size={16} className="text-rose-300 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-rose-100">
                Pending logs · {totalMissing} miss{totalMissing === 1 ? "" : "es"} across {missingByDate.length} day{missingByDate.length === 1 ? "" : "s"}
              </div>
              <div className="text-xs text-rose-200/60 font-mono mt-0.5">
                These teammates have not filed their daily log yet. They are hard-blocked from using LIM Flow on their next login.
              </div>
            </div>
          </button>
          {missingOpen && (
            <div className="divide-y divide-white/5">
              {missingByDate.map(({ date, pending }) => (
                <div key={date} className="px-4 py-3" data-testid={`team-logs-missing-${date}`}>
                  <div className="flex items-center gap-2 text-xs text-white/60 font-mono mb-2">
                    <CalendarDays size={12} className="text-rose-300/70" />
                    <span className="text-white/80">{fmtDate(date)}</span>
                    <span className="text-white/30">·</span>
                    <span>{weekdayLongIst(date)}</span>
                    <span className="ml-auto uppercase tracking-widest text-rose-300/80">
                      {pending.length} pending
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {pending.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => nav(`/daily-log?user_id=${u.id}&date=${date}`)}
                        data-testid={`team-logs-missing-chip-${date}-${u.id}`}
                        title={`Open ${u.name}'s log for ${date}`}
                        className="group inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 hover:border-rose-500/50 transition-colors"
                      >
                        <Avatar user={u} size={22} />
                        <span className="text-xs text-rose-100">{u.name}</span>
                        {u.team && (
                          <span className="text-[0.7rem] text-rose-200/50 font-mono uppercase tracking-widest">
                            · {u.team.replace(" Team", "")}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Day-grouped sections */}
      {loading ? (
        <div className="glass rounded-xl p-8 text-center text-white/40">Loading…</div>
      ) : daysDesc.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center text-white/40" data-testid="team-logs-empty">
          No logs in this date range.
        </div>
      ) : (
        <div className="space-y-3" data-testid="team-logs-days">
          {daysDesc.map(({ date, items, stats }) => {
            const isOpen = expanded.has(date);
            const isToday = date === today;
            return (
              <div key={date} className="glass rounded-xl overflow-hidden" data-testid={`team-logs-day-${date}`}>
                <button
                  onClick={() => toggleDay(date)}
                  data-testid={`team-logs-day-toggle-${date}`}
                  className={`w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left transition-colors ${
                    isOpen ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"
                  }`}
                >
                  {isOpen
                    ? <ChevronDown size={16} className="text-[#E11D2E] shrink-0" />
                    : <ChevronRight size={16} className="text-white/60 shrink-0" />}
                  <CalendarDays size={14} className="text-white/40 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm">
                      {fmtDate(date)}
                      <span className="text-white/40 text-xs ml-2 font-mono uppercase tracking-widest">
                        {weekdayLongIst(date)}
                      </span>
                      {isToday && (
                        <span className="ml-2 text-[0.7rem] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#E11D2E]/40 bg-[#E11D2E]/10 text-[#E11D2E] align-middle">
                          Today
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-auto flex flex-wrap gap-1.5 items-center">
                    <Chip tone="emerald" icon={CheckCircle2} label={`${stats.filed} filed`} />
                    {stats.leaves > 0 && <Chip tone="amber" icon={Coffee} label={`${stats.leaves} leave`} />}
                    {stats.otHalf > 0 && <Chip tone="red" icon={Clock} label={`${stats.otHalf} ½ OT`} />}
                    {stats.otFull > 0 && <Chip tone="red" icon={Clock} label={`${stats.otFull} full OT`} />}
                    {stats.otherWork > 0 && <Chip tone="white" icon={Sparkles} label={`${stats.otherWork} extra`} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="overflow-x-auto scrollbar-thin border-t border-white/5">
                    <table className="w-full text-sm min-w-[720px]">
                      <thead className="bg-black/20 text-white/60 text-[0.7rem] uppercase tracking-widest">
                        <tr>
                          <th className="text-left py-2.5 px-4">Member</th>
                          <th className="text-left py-2.5 px-4">Team</th>
                          <th className="text-left py-2.5 px-4">Status</th>
                          <th className="text-center py-2.5 px-4">Tasks</th>
                          <th className="text-center py-2.5 px-4">Other</th>
                          <th className="text-left py-2.5 px-4">Notes</th>
                          <th className="text-right py-2.5 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((r) => (
                          <tr
                            key={r.id || `${r.user_id}-${r.log_date}`}
                            onClick={() => openLog(r)}
                            data-testid={`team-logs-row-${r.id || r.user_id}`}
                            className="border-t border-white/5 hover:bg-white/[0.04] cursor-pointer transition-colors"
                          >
                            <td className="py-2 px-4">
                              <div className="flex items-center gap-2">
                                <Avatar user={r.user} size={22} />
                                <div className="min-w-0">
                                  <div className="text-xs truncate">{r.user.name}</div>
                                  <div className="text-[0.7rem] text-white/40 truncate">{r.user.title || r.user.role}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-4 text-xs text-white/60">{r.user.team}</td>
                            <td className="py-2 px-4 text-xs">
                              <StatusPill r={r} />
                            </td>
                            <td className="py-2 px-4 text-left text-xs font-mono text-white/70 align-top">
                              <TasksSummary r={r} logDate={date} taskById={taskById} />
                            </td>
                            <td className="py-2 px-4 text-center text-xs font-mono text-white/70">{r.extra_tasks?.length || 0}</td>
                            <td className="py-2 px-4 text-xs text-white/60 max-w-xs truncate" title={r.summary}>{r.summary || "—"}</td>
                            <td className="py-2 px-4 text-right whitespace-nowrap">
                              <button
                                onClick={(e) => { e.stopPropagation(); openLog(r); }}
                                title="Open & edit log"
                                data-testid={`team-logs-edit-${r.id}`}
                                className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/5"
                              >
                                <Pencil size={13} />
                              </button>
                              {user?.is_super_admin && (
                                <button
                                  onClick={(e) => deleteLog(r, e)}
                                  title="Delete log"
                                  data-testid={`team-logs-delete-${r.id}`}
                                  className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Chip({ tone, icon: Icon, label }) {
  const cls = tone === "emerald"
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
    : tone === "amber"
    ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
    : tone === "red"
    ? "border-[#E11D2E]/30 bg-[#E11D2E]/10 text-[#E11D2E]"
    : "border-white/15 bg-white/5 text-white/70";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[0.7rem] uppercase tracking-widest ${cls}`}>
      <Icon size={9} /> {label}
    </span>
  );
}

// Per-member breakdown of assigned-task outcomes for a log row. Renders one
// row per task_update showing the task title + a coloured status pill —
// managers wanted to see WHICH task got completed vs dropped, not just N/N.
// Per-member breakdown of THAT DAY's work: only tasks whose scheduled window
// (start_date → deadline) covers `logDate` are shown, alongside every extra
// (ad-hoc) work item the person recorded. Managers wanted this cell to be a
// literal answer to "what was on this person's plate today and how did it go".
function TasksSummary({ r, logDate, taskById = {} }) {
  const scheduled = (r.task_updates || []).filter((u) => {
    const meta = taskById[u.task_id];
    if (!meta) return false;               // task no longer exists → hide
    const start = meta.start_date || meta.deadline;
    const end = meta.deadline || meta.start_date;
    if (!start || !end) return false;      // Up-Next tasks (no dates) don't belong to any day
    return start <= logDate && logDate <= end;
  });
  const extras = (r.extra_tasks || []).filter((e) => (e.title || "").trim());
  if (scheduled.length === 0 && extras.length === 0) {
    return <span className="text-white/40">—</span>;
  }
  const tone = (s) => {
    return statusPill(s).replace("border border-", "border-");
  };
  const label = (s) => (s === "complete" ? "Done" : s === "incomplete" ? "Miss" : s === "on_hold" ? "Hold" : s === "in_progress" ? "WIP" : "New");
  return (
    <div
      data-testid={`team-logs-tasks-summary-${r.id || r.user_id}`}
      className="flex flex-col gap-1 items-start text-left max-w-[360px]"
    >
      {scheduled.map((u, i) => {
        const meta = taskById[u.task_id];
        const title = u.task_title || meta?.title || "Untitled task";
        return (
          <div
            key={`sched-${u.task_id || i}-${i}`}
            className="w-full flex items-center gap-2 min-w-0"
            title={`${title} — ${(u.status || "").replace("_", " ")}`}
          >
            <span className="flex-1 min-w-0 truncate text-xs text-white/85 normal-case tracking-normal">
              <ClientTag client={u.client || meta?.client} />{title}
            </span>
            <span
              className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full border text-[0.65rem] uppercase tracking-widest ${tone(u.status)}`}
            >
              {label(u.status)}
            </span>
          </div>
        );
      })}
      {extras.length > 0 && (
        <div className={`w-full ${scheduled.length > 0 ? "mt-1 pt-1 border-t border-white/5" : ""}`}>
          <div className="text-[0.65rem] uppercase tracking-widest text-white/40 mb-1">Extra work</div>
          {extras.map((e, i) => (
            <div
              key={`extra-${e.id || i}-${i}`}
              className="w-full flex items-center gap-2 min-w-0"
              title={`Extra — ${e.title}${e.client ? ` (${e.client})` : ""}`}
            >
              <span className="flex-1 min-w-0 truncate text-xs text-white/75 normal-case tracking-normal">
                <ClientTag client={e.client} />{e.title}
              </span>
              <span
                className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full border text-[0.65rem] uppercase tracking-widest ${tone(e.status || "complete")}`}
              >
                {label(e.status || "complete")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ r }) {
  if (r.on_leave) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[0.7rem] uppercase tracking-widest"><Coffee size={9} /> Leave</span>
    );
  }
  if (r.overtime === "half") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#E11D2E]/30 bg-[#E11D2E]/10 text-[#E11D2E] text-[0.7rem] uppercase tracking-widest">
        <Clock size={9} /> 1/2 OT{r.overtime_client ? ` · ${r.overtime_client}` : ""}{r.day_type && r.day_type !== "working" ? " · off day" : ""}
      </span>
    );
  }
  if (r.overtime === "full") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#E11D2E]/30 bg-[#E11D2E]/10 text-[#E11D2E] text-[0.7rem] uppercase tracking-widest">
        <Clock size={9} /> Full OT{r.overtime_client ? ` · ${r.overtime_client}` : ""}{r.day_type && r.day_type !== "working" ? " · off day" : ""}
      </span>
    );
  }
  if (r.day_type === "weekly_off") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-500/40 bg-slate-500/10 text-slate-200 text-[0.7rem] uppercase tracking-widest">Weekly Off</span>;
  }
  if (r.day_type === "holiday") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-200 text-[0.7rem] uppercase tracking-widest">Holiday</span>;
  }
  return <span className="text-white/40">On duty</span>;
}
